import { useMemo } from 'react';
import { useLinkStore } from '@/stores/linkStore';
import { RelationshipBadge } from './RelationshipBadge';
import { useEntityNameResolver } from '@/hooks/useEntityNameResolver';
import type { EntityType } from '@/types/link';

interface TaskLinkedEntitiesProps {
  taskId: string;
  maxDisplay?: number;
}

export function TaskLinkedEntities({ taskId, maxDisplay = 3 }: TaskLinkedEntitiesProps) {
  const { links } = useLinkStore();
  const { resolveEntityName } = useEntityNameResolver();

  const linkedEntities = useMemo(() => {
    return links
      .filter(link => 
        (link.source_type === 'task' && link.source_id === taskId) ||
        (link.target_type === 'task' && link.target_id === taskId)
      )
      .map(link => {
        const isSource = link.source_type === 'task';
        const type = (isSource ? link.target_type : link.source_type) as EntityType;
        const id = isSource ? link.target_id : link.source_id;
        return {
          id,
          type,
          name: resolveEntityName(type, id),
        };
      });
  }, [links, taskId, resolveEntityName]);

  if (linkedEntities.length === 0) return null;

  const displayEntities = linkedEntities.slice(0, maxDisplay);
  const remaining = linkedEntities.length - maxDisplay;

  return (
    <div className="flex flex-wrap gap-1 items-center">
      <span className="text-xs text-muted-foreground mr-1">Linked:</span>
      {displayEntities.map(entity => (
        <RelationshipBadge
          key={`${entity.type}-${entity.id}`}
          entityType={entity.type}
          entityId={entity.id}
          entityName={entity.name}
        />
      ))}
      {remaining > 0 && (
        <span className="text-xs text-muted-foreground">+{remaining} more</span>
      )}
    </div>
  );
}
